import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Container,
  Icon,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { Title, Loader, FarmSelectModal, FarmSelectModalHandle } from '../../elements';
import { VetService } from '../../hooks/VetService';
import { LoaderSize } from '../../constants/constants';
import { CreateUpdateVetContactModal, CreateUpdateVeterinaryModal } from '../../components';
import { useTranslation } from 'react-i18next';
import { VeterinaryContact, VeterinaryViewModel } from '../../models/vets';
import { notifications } from '../../utils';
import { useConfirmation } from '../../hooks/ConfirmationService';
import useAuthContext from '../../hooks/useAuthContext';
import UserRole from '../../constants/user-role';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { HealthSystemDomainFarmsResultFarmViewModel } from '../../api/HealthSystemApi';

const VeterinaryView: React.FC<any> = () => {
  const { state } = useAuthContext();
  const styles = sxStyles(useTheme());
  const vetService = new VetService();
  const { t } = useTranslation();
  const { id } = useParams();
  const confirmDialog = useConfirmation();

  const [veterinary, setVeterinary] = React.useState({} as VeterinaryViewModel);
  const [updateVetDialogOpen, setUpdateVetDialogOpen] = React.useState(false);
  const [vetContactMode, setVetContactMode] = React.useState('create' as 'create' | 'update');
  const [addVetContactDialogOpen, setAddVetContactDialogOpen] = React.useState(false);
  const [selectedVetContact, setSelectedVetContact] = React.useState(null as VeterinaryContact | null);

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);

  const createUpdateVeterinaryModalRef = React.useRef<HTMLElement>(null!);
  const createUpdateVetContactModalRef = React.useRef<HTMLElement>(null!);
  const farmSelectModalRef = React.useRef<FarmSelectModalHandle>(null!);

  const loadVeterinary = async () => {
    setIsBusy(true);
    setLoaded(false);

    if (!id) {
      notifications.error('Vet ID is undefined.');
      return;
    }

    await vetService
      .getVeterinary(id)
      .then((vet) => {
        setVeterinary(vet);
        setLoaded(true);
      })
      .catch((e) => {
        setLoaded(false);
        notifications.error(e?.error?.message ?? e.toString());
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    loadVeterinary();
  }, [id]);

  React.useEffect(() => {
    if (!loaded) {
      loadVeterinary();
    }
  }, [loaded]);

  // common handler for both actions, split if logic will be separated
  const handleCreateUpdateVetOrContact = (result: boolean | null) => {
    setUpdateVetDialogOpen(false);
    setAddVetContactDialogOpen(false);

    if (result) {
      setLoaded(false);
    }
  };

  const handleAddContact = () => {
    setSelectedVetContact(null);
    setVetContactMode('create');
    setAddVetContactDialogOpen(true);
  };

  const handleAddFarmClick = () => {
    farmSelectModalRef.current.openModal();
  };

  const handleAddFarm = (farm: HealthSystemDomainFarmsResultFarmViewModel | null) => {
    if (!farm) {
      return;
    }

    vetService
      .assignFarmToVet(farm.id, veterinary.id)
      .then(() => {
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(e?.error?.message ?? e.toString());
      });
  };

  const handleUpdateContact = (contact: VeterinaryContact) => {
    setSelectedVetContact(contact);
    setVetContactMode('update');
    setAddVetContactDialogOpen(true);
  };

  const handleDeleteContact = (contact: VeterinaryContact) => {
    confirmDialog({
      dialogId: 'delete-con-dialog',
      title: t('f-vets-con-del-title'),
      message: t('f-vets-con-del-msg', { text: contact.name }),
      alertSeverity: 'warning'
    }).then(() => {
      vetService
        .deleteVetContact(contact.id, veterinary.id)
        .then(() => {
          setLoaded(false);
          notifications.success(t('f-vets-con-del-ok'));
        })
        .catch((e) => {
          notifications.error(e?.error?.message ?? e.toString());
        });
    });
  };

  const handleDeleteFarm = (farm: HealthSystemDomainFarmsResultFarmViewModel) => {
    confirmDialog({
      dialogId: 'delete-farm-dialog',
      title: t('f-vets-farm-del-title'),
      message: t('f-vets-farm-del-msg', { text: farm.name }),
      alertSeverity: 'warning'
    }).then(() => {
      vetService
        .unlinkFarmFromVet(farm.id, veterinary.id)
        .then(() => {
          setLoaded(false);
        })
        .catch((e) => {
          notifications.error(e?.error?.message ?? e.toString());
        });
    });
  };

  const renderFarmContactCardAction = (farm: HealthSystemDomainFarmsResultFarmViewModel) => {
    return (
      <Grid container spacing={1} direction="column">
        <Grid item>
          <Tooltip arrow title={t('f-com-remove')}>
            <IconButton size="small" color="primary" onClick={() => handleDeleteFarm(farm)} sx={V4_ICON_BUTTON_SMALL}>
              <Icon>{'link_off'}</Icon>
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Grid container spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex' }}>
              <Icon color="primary" fontSize="large">
                {'local_pharmacy'}
              </Icon>
              <Box sx={{ ml: 1, pt: 0.5 }}>
                <Title>{t('f-vets-view-title')}</Title>
              </Box>
            </Box>
            <Box>
              <IconButton
                color="secondary"
                size="medium"
                aria-label={t('f-com-edit')}
                onClick={() => setUpdateVetDialogOpen(true)}
                sx={V4_ICON_BUTTON}
              >
                <Icon>edit</Icon>
              </IconButton>

              <IconButton
                color="primary"
                size="medium"
                aria-label={t('f-com-create-new')}
                onClick={() => setLoaded(false)}
                sx={V4_ICON_BUTTON}
              >
                <Icon>refresh</Icon>
              </IconButton>
            </Box>
          </Box>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <Paper sx={styles.vetCard} elevation={1}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} lg={4} sx={styles.mb2}>
                  <Grid item container spacing={4}>
                    <Grid item container xs={4} sx={styles.movingText}>
                      <Typography>{t('f-com-number')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography sx={styles.bold}>{veterinary.number}</Typography>
                    </Grid>
                  </Grid>

                  <Grid item container spacing={4}>
                    <Grid item container xs={4} sx={styles.movingText}>
                      <Typography>{t('f-com-name')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography sx={styles.bold}>{veterinary.name}</Typography>
                    </Grid>
                  </Grid>

                  <Grid item container spacing={4}>
                    <Grid item container xs={4} sx={styles.movingText}>
                      <Typography>{t('f-com-created')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography sx={styles.bold}>{new Date(veterinary.created).toLocaleString()}</Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={6} lg={4} sx={styles.mb2}>
                  <Grid item container spacing={4}>
                    <Grid item container xs={4} sx={styles.movingText}>
                      <Typography>{t('f-country')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography sx={styles.bold}>{veterinary.settings.country || '-'}</Typography>
                    </Grid>
                  </Grid>

                  <Grid item container spacing={4}>
                    <Grid item container xs={4} sx={styles.movingText}>
                      <Typography>{t('f-com-address')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography sx={styles.bold}>{veterinary.settings.address || '-'}</Typography>
                    </Grid>
                  </Grid>

                  <Grid item container spacing={4}>
                    <Grid item container xs={4} sx={styles.movingText}>
                      <Typography>{t('f-com-phone')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Link href={`tel:${veterinary.settings.phone}`} variant="body1" sx={styles.bold}>
                        {veterinary.settings.phone}
                      </Link>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                  <Grid item container spacing={4}>
                    <Grid item container xs={4} sx={styles.movingText}>
                      <Typography>{t('f-vets-mail-back')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Link href={`mailto:${veterinary.settings.email}`} variant="body1" sx={styles.bold}>
                        {veterinary.settings.email}
                      </Link>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Grid item md={6} xs={12} sx={styles.mt2}>
            <Grid container direction="column" spacing={1}>
              <Grid item md={12} id="vetFarmsView">
                <Grid container direction="row" alignItems="center" spacing={1} sx={styles.mh68}>
                  <Grid item id="farmsIcon">
                    <Icon color="primary" fontSize="large">
                      {'home_work'}
                    </Icon>
                  </Grid>

                  <Grid item>
                    <Title>{t('f-farms')}</Title>
                  </Grid>

                  <Grid item>
                    <Chip label={veterinary?.farms?.length} variant="outlined" />
                  </Grid>

                  {state.role === UserRole.admin && (
                    <Grid item sx={styles.headerRightSection}>
                      <Tooltip arrow title={t('f-vets-farm-add')}>
                        <IconButton color="primary" onClick={handleAddFarmClick} sx={V4_ICON_BUTTON}>
                          <Icon fontSize="large">{'add_circle'}</Icon>
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>

                {loaded &&
                  veterinary.farms &&
                  veterinary.farms.map((farm, index) => (
                    <Paper variant="outlined">
                      <List>
                        <div key={index}>
                          <ListItem alignItems="flex-start">
                            <ListItemAvatar>
                              <Avatar>{farm.name?.[0]}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <React.Fragment>
                                  <Typography variant="subtitle1" sx={styles.bold}>
                                    {farm.name}
                                  </Typography>
                                  <Typography variant="body2">{farm.number}</Typography>
                                  <Typography variant="body1">{farm.address}</Typography>
                                </React.Fragment>
                              }
                            />
                            <ListItemSecondaryAction>{renderFarmContactCardAction(farm)}</ListItemSecondaryAction>
                          </ListItem>
                        </div>
                      </List>
                    </Paper>
                  ))}

                {loaded && veterinary.farms.length === 0 && <Alert severity="warning">{t('f-no-data')}</Alert>}
              </Grid>
            </Grid>

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
          </Grid>

          <Grid item md={6} xs={12} sx={styles.mt2}>
            <Grid container direction="column" spacing={1}>
              <Grid item md={12} id="vetContactsView">
                <Grid container direction="row" alignItems="center" spacing={1} sx={styles.mh68}>
                  <Grid item id="vetIcon">
                    <Icon color="primary" fontSize="large">
                      {'contacts'}
                    </Icon>
                  </Grid>

                  <Grid item>
                    <Title>{t('f-vets-contacts')}</Title>
                  </Grid>

                  <Grid item>
                    <Chip label={veterinary?.contacts?.length} variant="outlined" />
                  </Grid>
                  <Grid item sx={styles.headerRightSection}>
                    <Tooltip arrow title={t('f-settings-upd-con-add')}>
                      <IconButton color="primary" onClick={handleAddContact} sx={V4_ICON_BUTTON}>
                        <Icon fontSize="large">{'add_circle'}</Icon>
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>

                {loaded &&
                  veterinary.contacts &&
                  veterinary.contacts.map((con, index) => (
                    <Paper variant="outlined">
                      <List>
                        <div key={index}>
                          <ListItem alignItems="flex-start">
                            <ListItemAvatar>
                              <Avatar>{con.name[0]}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <React.Fragment>
                                  <Typography variant="subtitle1" sx={styles.bold}>
                                    {con.name}
                                  </Typography>
                                  <Typography variant="body2">{con.email}</Typography>
                                  <Typography variant="body1">{con.phone}</Typography>
                                </React.Fragment>
                              }
                            />
                            <ListItemSecondaryAction>
                              {con.isPrimary && (
                                <IconButton
                                  size="small"
                                  color="default"
                                  disabled
                                  style={{ marginRight: 8 }}
                                  sx={V4_ICON_BUTTON_SMALL}
                                >
                                  <Icon>{'star'}</Icon>
                                </IconButton>
                              )}

                              <Tooltip arrow title={t('f-com-edit')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleUpdateContact(con)}
                                  style={{ marginRight: 8 }}
                                  sx={V4_ICON_BUTTON_SMALL}
                                >
                                  <Icon>{'settings'}</Icon>
                                </IconButton>
                              </Tooltip>

                              <Tooltip arrow title={t('f-com-remove')}>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleDeleteContact(con)}
                                  sx={V4_ICON_BUTTON_SMALL}
                                >
                                  <Icon>{'delete_outline'}</Icon>
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </div>
                      </List>
                    </Paper>
                  ))}

                {loaded && veterinary.contacts.length === 0 && <Alert severity="warning">{t('f-no-data')}</Alert>}
              </Grid>
            </Grid>

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
          </Grid>
        </Grid>
      </Container>

      <CreateUpdateVeterinaryModal
        ref={createUpdateVeterinaryModalRef}
        opened={updateVetDialogOpen}
        onModalAction={handleCreateUpdateVetOrContact}
        mode={'update'}
        vet={veterinary}
      />

      <CreateUpdateVetContactModal
        ref={createUpdateVetContactModalRef}
        opened={addVetContactDialogOpen}
        onModalAction={handleCreateUpdateVetOrContact}
        mode={vetContactMode}
        contact={selectedVetContact}
        veterinaryId={veterinary?.id}
      />

      <div className="hidden">
        <FarmSelectModal ref={farmSelectModalRef} farm={null} disableAny onChange={(farm) => handleAddFarm(farm)} />
      </div>
    </>
  );
};

export default VeterinaryView;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    formControl: {
      margin: theme.spacing(1)
    },
    bold: {
      fontWeight: 'bold'
    },
    headerRightSection: {
      flex: 1,
      textAlign: 'right',
      flexDirection: 'row-reverse'
    },
    displayNone: {
      display: 'none'
    },
    badge: {
      right: -3,
      top: 13,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px'
    },
    mb3: {
      marginBottom: theme.spacing(3)
    },
    mb2: {
      marginBottom: theme.spacing(2)
    },
    mt3: {
      marginTop: theme.spacing(3)
    },
    mt2: {
      marginTop: theme.spacing(2)
    },
    pl3: {
      paddingLeft: theme.spacing(3)
    },
    movingText: {
      '@media (min-width:960px)': {
        // v4's md; v9's is 900px
        // background: "red",
        justifyContent: 'flex-end',
        textAlign: 'end'
      }
    },
    vetCard: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(3),
      width: '100%'
    },
    mh68: {
      minHeight: 68
    }
  }) as const;
